import {useState, useEffect} from 'react'
import {useBi, useEnvironment, useExperiments, useTranslation} from '@wix/yoshi-flow-editor'
import {Reservation, Status} from '@wix/ambassador-table-reservations-v1-reservation/types'

import {useReservationConfirmationStorage} from '../storage'
import {getReservationLocationById} from '../../../utils/reservationLocation'
import {useGetLayoutSize} from '../../../components-shared/LayoutSizeProvider'
import {getShouldShowAutoApproveText} from '../../../utils/getShouldShowAutoApproveText'
import {RequestStatus} from '../../../utils/wrapRequest'
import {
  CHECKOUT_PAGE_REFERRER,
  EXPERIMENTS,
  GoogleMapsConversionCollectExperimentOptions,
  GoogleMapsConversionCollectExperimentOptionsTypes,
  RESERVATION_DETAILS_REFERRER,
} from '../../../utils/constants'
import {usePaymentDetails} from '../../../utils/usePaymentDetails'
import {useExperiencePaymentDetails} from '../../../utils/useExperiencePaymentDetails'
import {isFreeOrder} from '../../../utils/ecom'
import {getLogger} from '../../../utils/getLogger'
import {getRwgToken} from '../../../utils/rwgToken'
import {triggerGoogleMapsConversionCollect} from '../../../services/googleService'

import {getCalendarHref, getMessageType} from './utils'

const getUrlParam = (paramName: string): string | null => {
  if (typeof window === 'undefined') {
    return null
  }
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get(paramName)
}

export const useHooks = () => {
  const {
    reservation,
    reservationLocations,
    orderDetails,
    experience,
    goToNewReservation,
    regionalSettings,
    approvalTextEditorState,
    isManualApproval,
    getReservationStatus,
    getReservationLocationsStatus,
    trackTableReservedEvent,
    trackPurchaseEvent,
  } = useReservationConfirmationStorage()

  const bi = useBi()
  const logger = getLogger(bi)

  const {experiments} = useExperiments()
  const googleMapsConversionCollectExperiment = experiments.get(
    EXPERIMENTS.googleMapsConversionCollect,
  ) as GoogleMapsConversionCollectExperimentOptionsTypes

  if (
    reservation?.status === Status.PAYMENT_PENDING ||
    reservation?.status === Status.PAYMENT_INFORMATION_PENDING
  ) {
    reservation.status = isManualApproval ? Status.REQUESTED : Status.RESERVED
  }

  const isReservationRequestFailed = getReservationStatus === RequestStatus.FAILED
  const isReservationLocationRequestFailed = getReservationLocationsStatus === RequestStatus.FAILED

  const shouldShowErrorModal = isReservationRequestFailed || isReservationLocationRequestFailed

  const {isViewer, isPreview} = useEnvironment()

  const {t} = useTranslation()
  const [isCancelDialogOpened, setIsCancelDialogOpened] = useState<boolean>(false)

  const isLoading = getReservationStatus === RequestStatus.LOADING

  const currentReservationLocation = getReservationLocationById(
    reservation?.details?.reservationLocationId ?? '',
    reservationLocations,
  )

  const titleCalendarEvent = experiments.enabled(EXPERIMENTS.newTitleInCalendarEvent)
    ? t('uou-reservations.reservation-confirmation.calendar-title', {
        locationName: currentReservationLocation?.location?.name,
      })
    : currentReservationLocation?.location?.name ?? ''

  const openCancelDialog = () => {
    setIsCancelDialogOpened(true)
  }

  const closeCancelDialog = () => {
    setIsCancelDialogOpened(false)
  }

  const {layoutSize} = useGetLayoutSize()

  const shouldShowAutoApproveText = getShouldShowAutoApproveText(
    approvalTextEditorState,
    isManualApproval,
  )

  const shouldShowOrderDetails =
    orderDetails?.lineItems?.[0].quantity &&
    Number(orderDetails.lineItems[0].lineItemPrice?.amount) > 0

  if (reservation) {
    reservation.status = isViewer
      ? reservation.status
      : shouldShowAutoApproveText
      ? Status.RESERVED
      : Status.REQUESTED
  }

  const reservationLocationPaymentDetails = usePaymentDetails(
    currentReservationLocation,
    reservation?.details?.partySize,
  )

  const experiencePaymentDetails = useExperiencePaymentDetails(
    experience,
    reservation?.details?.partySize,
  )

  const {isPerGuestPayment, pricePerUnitWithCurrency} = experience
    ? experiencePaymentDetails
    : reservationLocationPaymentDetails

  const onAddToCalendarClick = () => {
    logger.clickOnAddToCalendar({
      reservation: reservation as Reservation,
      locationId: currentReservationLocation?.location?.id,
      reservationLocationId: currentReservationLocation?.id,
      isPreview,
      isSuccessful: true,
    })
  }

  useEffect(() => {
    // Free reservation events will be sent in Step 2 after the reservation creation request
    if (reservation?.id) {
      if (orderDetails && !isFreeOrder(orderDetails)) {
        trackTableReservedEvent(reservation.id, reservation.reservee)
        trackPurchaseEvent(reservation.id, orderDetails)
      }

      if (
        googleMapsConversionCollectExperiment ===
          GoogleMapsConversionCollectExperimentOptions.sandbox ||
        googleMapsConversionCollectExperiment === GoogleMapsConversionCollectExperimentOptions.prod
      ) {
        const rwgToken = getRwgToken()
        const referrerParam = getUrlParam('referrer')
        const isReservationDetailsReferrer = referrerParam === RESERVATION_DETAILS_REFERRER
        const isCheckoutReferrer = referrerParam === CHECKOUT_PAGE_REFERRER

        if (rwgToken && (isReservationDetailsReferrer || isCheckoutReferrer)) {
          triggerGoogleMapsConversionCollect(rwgToken, googleMapsConversionCollectExperiment)
        }
      }
    }
  }, [reservation?.id, orderDetails])

  return {
    t,
    layoutSize,
    isCancelDialogOpened,
    reservation,
    regionalSettings,
    orderDetails,
    shouldShowOrderDetails,
    experience,
    goToNewReservation,
    openCancelDialog,
    closeCancelDialog,
    calendarHref: getCalendarHref(titleCalendarEvent, currentReservationLocation, reservation),
    messageType: getMessageType(reservation?.reservee ?? ''),
    formattedAddress: currentReservationLocation?.location?.address?.formattedAddress ?? '',
    locationName: currentReservationLocation?.location?.name ?? '',
    timeZone: currentReservationLocation?.location?.timeZone,
    phoneNumber: currentReservationLocation?.location?.phone,
    isLoading,
    isPerGuestPayment,
    pricePerUnitWithCurrency,
    onAddToCalendarClick,
    shouldShowErrorModal,
  }
}
